import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookCover from '../components/BookCover';
import FlipbookViewer from '../components/FlipbookViewer';
import GiftOnboarding from '../components/GiftOnboarding';
import { Toaster } from 'sonner';
import { BookOpen, Loader2, ArrowLeft, Gift } from 'lucide-react';
import { loginWithGoogle } from '../utils/auth';
import { BACKEND_URL, API } from '../utils/api';
import { authFetch, clearToken } from '../utils/session';

const R2_URL = process.env.REACT_APP_R2_URL || BACKEND_URL + "/api/static";

const LibraryPage = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [giftedSlugs, setGiftedSlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeBook, setActiveBook] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(null);
  const [completedSurveys, setCompletedSurveys] = useState({});

  useEffect(() => {
    Promise.all([
      authFetch(`${API}/auth/me`, {  }).then(r => r.ok ? r.json() : null),
      authFetch(`${API}/user/library`, {  }).then(r => r.ok ? r.json() : []),
      authFetch(`${API}/user/gifted`, {  }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/books`).then(r => r.json()),
    ]).then(([u, lib, gifted, bks]) => {
      setUser(u);
      setGiftedSlugs(gifted || []);
      const mapped = (bks || []).map(b => ({ ...b, id: b.book_id || b.slug, coverUrl: `${R2_URL}/books/${b.imageFolder}/cover.webp`, imagePages: b.pageCount, imageFolder: b.imageFolder }));
      setBooks(mapped.filter(b => (lib || []).includes(b.slug)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const purchased = books.filter(b => !giftedSlugs.includes(b.slug));
  const gifted = books.filter(b => giftedSlugs.includes(b.slug));

  if (loading) return <div className="min-h-dvh flex items-center justify-center"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;

  if (!user) {
    return (
      <div className="min-h-dvh bg-background font-sans">
        <Header />
        <div className="max-w-md mx-auto px-6 pt-20 text-center">
          <BookOpen className="size-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm mb-4">Inicia sesión para ver tu biblioteca</p>
          <button onClick={() => loginWithGoogle('/biblioteca')} className="h-10 px-5 rounded-xl bg-foreground text-background text-sm font-medium">Iniciar sesión con Google</button>
        </div>
      </div>
    );
  }

  const BookGrid = ({ items, emptyText }) => items.length > 0 ? (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
      {items.map(book => (
        <button key={book.slug} onClick={() => setActiveBook(book)} className="text-left group">
          <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2 group-hover:scale-105 transition-transform duration-200 shadow-sm">
            <BookCover book={book} hideTitle />
          </div>
          <p className="text-xs font-medium truncate">{book.title}</p>
        </button>
      ))}
    </div>
  ) : <p className="text-sm text-muted-foreground/50 py-6">{emptyText}</p>;

  return (
    <div className="min-h-dvh bg-background font-sans">
      <Toaster position="top-center" />
      <Header />
      <div className="max-w-4xl mx-auto px-6 pt-6 pb-16">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="size-4" /> Volver
        </button>
        <h1 className="text-xl font-bold mb-8">Mi Biblioteca</h1>

        {books.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="size-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">Aún no tienes libros</p>
            <button onClick={() => navigate('/')} className="mt-4 text-sm text-foreground font-medium hover:underline">Explorar libros</button>
          </div>
        ) : (
          <>
            {/* Mis Libritos */}
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="size-4 text-muted-foreground/50" />
                <h2 className="text-sm font-semibold">Mis Libritos</h2>
                <span className="text-[10px] text-muted-foreground/40">{purchased.length}</span>
              </div>
              <BookGrid items={purchased} emptyText="Aún no has comprado libritos" />
            </div>

            {/* Mis Regalos */}
            {gifted.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Gift className="size-4 text-amber-500/70" />
                  <h2 className="text-sm font-semibold">Mis Regalos</h2>
                  <span className="text-[10px] text-muted-foreground/40">{gifted.length}</span>
                </div>
                <BookGrid items={gifted} emptyText="" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Flipbook opens directly */}
      {activeBook && (
        <FlipbookViewer
          book={activeBook}
          isGift={giftedSlugs.includes(activeBook.slug)}
          onClose={() => {
            const closedBook = activeBook;
            setActiveBook(null);
            // If it's a gifted book and survey not yet completed, show onboarding
            if (giftedSlugs.includes(closedBook.slug) && !completedSurveys[closedBook.slug]) {
              authFetch(`${API}/surveys/gift/check/${closedBook.slug}`)
                .then(r => r.ok ? r.json() : { completed: false })
                .then(data => {
                  if (!data.completed) {
                    setShowOnboarding(closedBook);
                  } else {
                    setCompletedSurveys(prev => ({ ...prev, [closedBook.slug]: true }));
                  }
                })
                .catch(() => {});
            }
          }}
        />
      )}

      {/* Gift Onboarding */}
      {showOnboarding && (
        <GiftOnboarding
          book={showOnboarding}
          onClose={() => {
            setCompletedSurveys(prev => ({ ...prev, [showOnboarding.slug]: true }));
            setShowOnboarding(null);
          }}
        />
      )}

      <Footer />
    </div>
  );
};

export default LibraryPage;
